"use strict";
/**
 * Rules for template-level Custom Fields (`ec__document_template.ec__custom_data`).
 *
 * These rows are typed (`string` | `numeric` | `date`) and `ec__label` is unique. That is the
 * opposite of element Configuration Data, where repeated keys are expected and the pair is
 * untyped `{key, value}`. Do not reuse `element_custom_data.ts` here.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.projectableTemplateCustomFields = exports.firstTemplateCustomFieldValidationError = exports.collectTemplateCustomFieldValidationErrors = exports.templateCustomFieldError = exports.TEMPLATE_CUSTOM_FIELD_MESSAGES = exports.templateCustomFieldGap = exports.duplicateTemplateCustomFieldLabels = exports.normalizeTemplateCustomFields = exports.isBlankTemplateCustomField = void 0;
const trimField = (field) => (Object.assign(Object.assign({}, field), { ec__label: field.ec__label.trim(), ec__value: field.ec__value.trim() }));
/**
 * A row the author added and then left alone. Adding a field is one click, so an abandoned blank
 * row is debris rather than a mistake worth reporting.
 */
const isBlankTemplateCustomField = (field) => {
    const trimmed = trimField(field);
    return trimmed.ec__label === '' && trimmed.ec__value === '';
};
exports.isBlankTemplateCustomField = isBlankTemplateCustomField;
/**
 * Trims every field and drops the blank ones, so a template never persists rows that mean nothing.
 *
 * Half-finished rows are kept: dropping them would silently discard a label the author started.
 * Those are reported in the editor and block Close/Publish instead.
 */
const normalizeTemplateCustomFields = (fields) => fields.map(trimField).filter((field) => !(0, exports.isBlankTemplateCustomField)(field));
exports.normalizeTemplateCustomFields = normalizeTemplateCustomFields;
const isNumericValue = (value) => /^-?\d+(\.\d+)?$/.test(value);
const isDateValue = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return false;
    }
    const [year, month, day] = value.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);
    return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
};
const duplicateTemplateCustomFieldLabels = (fields) => {
    const seen = new Set();
    const duplicates = new Set();
    for (const field of fields) {
        const label = field.ec__label.trim();
        if (!label) {
            continue;
        }
        if (seen.has(label)) {
            duplicates.add(label);
        }
        seen.add(label);
    }
    return duplicates;
};
exports.duplicateTemplateCustomFieldLabels = duplicateTemplateCustomFieldLabels;
/**
 * Why this field cannot be published, or `null` when it is blank or complete.
 *
 * Blank rows are not a gap — they are dropped on persist. A duplicate label is reported on every
 * copy so the author can see both sides of the clash, matching the editor card.
 */
const templateCustomFieldGap = (field, duplicateLabels) => {
    if ((0, exports.isBlankTemplateCustomField)(field)) {
        return null;
    }
    const trimmed = trimField(field);
    if (trimmed.ec__label !== '' && duplicateLabels.has(trimmed.ec__label)) {
        return 'duplicate';
    }
    if (trimmed.ec__label === '') {
        return 'label';
    }
    if (trimmed.ec__value === '') {
        return 'value';
    }
    if (trimmed.ec__type === 'numeric' && !isNumericValue(trimmed.ec__value)) {
        return 'numeric';
    }
    if (trimmed.ec__type === 'date' && !isDateValue(trimmed.ec__value)) {
        return 'date';
    }
    return null;
};
exports.templateCustomFieldGap = templateCustomFieldGap;
exports.TEMPLATE_CUSTOM_FIELD_MESSAGES = {
    missingLabel: 'Give "{value}" a label or remove the field.',
    missingValue: 'Give "{label}" a value or remove the field.',
    duplicateLabel: 'Labels must be unique',
    numericFormat: '"{label}" must be a number.',
    dateFormat: '"{label}" must be a date (YYYY-MM-DD).',
};
const templateCustomFieldError = (field, gap) => {
    const trimmed = trimField(field);
    switch (gap) {
        case 'label':
            return { message: exports.TEMPLATE_CUSTOM_FIELD_MESSAGES.missingLabel, params: { value: trimmed.ec__value } };
        case 'value':
            return { message: exports.TEMPLATE_CUSTOM_FIELD_MESSAGES.missingValue, params: { label: trimmed.ec__label } };
        case 'duplicate':
            return { message: exports.TEMPLATE_CUSTOM_FIELD_MESSAGES.duplicateLabel };
        case 'numeric':
            return { message: exports.TEMPLATE_CUSTOM_FIELD_MESSAGES.numericFormat, params: { label: trimmed.ec__label } };
        case 'date':
            return { message: exports.TEMPLATE_CUSTOM_FIELD_MESSAGES.dateFormat, params: { label: trimmed.ec__label } };
    }
};
exports.templateCustomFieldError = templateCustomFieldError;
/**
 * Every field that would block Close or Publish, in field order. Blank rows are ignored — they are
 * dropped rather than refused.
 */
const collectTemplateCustomFieldValidationErrors = (fields = []) => {
    const trimmed = fields.map(trimField);
    const duplicates = (0, exports.duplicateTemplateCustomFieldLabels)(trimmed);
    const errors = [];
    for (const field of trimmed) {
        const gap = (0, exports.templateCustomFieldGap)(field, duplicates);
        if (gap) {
            errors.push((0, exports.templateCustomFieldError)(field, gap));
        }
    }
    return errors;
};
exports.collectTemplateCustomFieldValidationErrors = collectTemplateCustomFieldValidationErrors;
/**
 * First field that would block Close or Publish. Blank rows are ignored — they are dropped rather
 * than refused. Used by the editor so Close and Publish share one rule.
 */
const firstTemplateCustomFieldValidationError = (fields = []) => { var _a; return (_a = (0, exports.collectTemplateCustomFieldValidationErrors)(fields)[0]) !== null && _a !== void 0 ? _a : null; };
exports.firstTemplateCustomFieldValidationError = firstTemplateCustomFieldValidationError;
/** Complete, unique, type-valid fields — the shape Cortex stores on the template and copies onto signed documents. */
const projectableTemplateCustomFields = (fields) => {
    const normalized = (0, exports.normalizeTemplateCustomFields)(fields);
    const duplicates = (0, exports.duplicateTemplateCustomFieldLabels)(normalized);
    return normalized.filter((field) => (0, exports.templateCustomFieldGap)(field, duplicates) === null);
};
exports.projectableTemplateCustomFields = projectableTemplateCustomFields;
//# sourceMappingURL=template_custom_data.js.map